import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, shiftsService } from '../services/api';
import { useAuth } from '../hooks/useAuth';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [eventName, setEventName] = useState<string>('Roskilde Festival');
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    shiftsService.getSettings().then((response) => {
      if (response?.data?.event_name) {
        setEventName(response.data.event_name);
      }
    }).catch(() => {
      // Use default if settings fetch fails
    });
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (user) {
      navigate('/');
    }
  }, [authLoading, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await authService.requestLogin(email);

      // In dev bypass mode the backend returns a JWT and the token; prefer immediate login
      const jwtToken = res.data?.jwtToken;
      if (jwtToken) {
        localStorage.setItem('token', jwtToken);
        navigate('/');
        return;
      }

      // Otherwise, use the token-based flow
      const token = res.data?.loginToken;
      if (token) {
        navigate(`/login/${token}`);
        return;
      }

      setSent(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send login link');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <h2 className="text-2xl font-bold mb-4">Check Your Email</h2>
          <p className="text-gray-600 mb-6">
            We've sent a login link to <strong>{email}</strong>. Click the link
            to log in.
          </p>
          <button
            onClick={() => {
              setSent(false);
              setEmail('');
            }}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        <h1 className="text-3xl font-bold mb-2">🎪 Volunteer Manager</h1>
        <p className="text-gray-600">{eventName}</p>
        <p className="text-gray-700 mb-6">
          <strong>New here and want to apply as a volunteer?</strong> Enter your email to get started.
        <br />
            <strong>Already signed up as a volunteer?</strong> Use the same email to log in.
        </p>

        <form onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? 'Sending...' : 'Send Login Link'}
          </button>
        </form>
      </div>
    </div>
  );
}
